/**
 * The opportunity record types the sync can be restricted to, as a `PropertyFilter` query.
 *
 * Same control and layout as the date rows and the industry rows, so the whole page reads as
 * one thing rather than three. The tokens ARE the configuration here, exactly as they are for
 * a date column.
 *
 * The candidate list is derived from the generated `OpportunityRecordType`, not copied, so the
 * picker cannot offer a value the service would reject. Declaration order is the model's,
 * which puts Utility first.
 */

import type { PropertyFilterProps } from '@amzn/awsui-components-react';
import { OpportunityRecordType } from '../../client/IvtHubClient';

/** The single property, so a token reads `record type = Utility`. */
export const RECORD_TYPE_KEY = 'record_type';

const ALL = Object.values(OpportunityRecordType);

export const RECORD_TYPE_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    key: RECORD_TYPE_KEY,
    propertyLabel: 'record type',
    groupValuesLabel: 'Record types',
    // `=` only, for the same reason as the industry nodes: no other operator can be stored.
    operators: ['='],
  },
];

export const RECORD_TYPE_OPTIONS: PropertyFilterProps.FilteringOption[] = ALL.map((t) => ({
  propertyKey: RECORD_TYPE_KEY,
  value: t,
}));

/** Selected types → tokens, order preserved so the row reads in the stored order. */
export function recordTypesToQuery(
  types: readonly string[] | undefined,
): PropertyFilterProps.Query {
  return {
    operation: 'or',
    tokens: (types ?? []).map((t) => ({
      propertyKey: RECORD_TYPE_KEY,
      operator: '=',
      value: t,
    })),
  };
}

/**
 * Tokens → selected types.
 *
 * Drops anything outside the enum with a real membership check rather than a cast: typing is
 * possible in a `PropertyFilter`, so this is where a value that is not a record type would
 * otherwise reach the configuration. Also de-duplicates, since two identical tokens would
 * store a repeated value.
 */
export function queryToRecordTypes(query: PropertyFilterProps.Query): OpportunityRecordType[] {
  const out: OpportunityRecordType[] = [];
  for (const t of query.tokens) {
    if (t.propertyKey !== RECORD_TYPE_KEY) continue;
    const value = typeof t.value === 'string' ? t.value : undefined;
    if (!value || !ALL.includes(value as OpportunityRecordType)) continue;
    if (!out.includes(value as OpportunityRecordType)) {
      out.push(value as OpportunityRecordType);
    }
  }
  return out;
}
